<template>

  <memlist-modal size="xxl" ref="internal-event-editor-modal">

    <template #header>
      <a v-if="local_item.event_id" @click.prevent="goto_url_clicked" target="_blank" class="ml-4 mt-2 legacy-link d-inline-flex align-items-center text-decoration-none">
        <span class="svg-icon svg-icon-md text-primary me-2">
          <i class="fa fa-link color-primary"></i>
        </span>
        <span class="text-primary">{{ event_url }}</span>
      </a>
    </template>

    <div ref="page-container" class="page-container">
      <div ref="top-container" class="top-container">

        <ErrorPopup
          :error="error_popup_message"
        />

        <div class="left-menu" ref="navigation-drawer">
          <memlist-navigation-drawer
            :items="unlocked_sections.filter(s => s.hide_on_create ? !creating : true).map(s => ({ icon: s.icon, value: s.value, text: s.title }))"
            @clicked="on_drawer_item_click"
          />
          <div v-if="local_item_status === 'DRAFT' || local_item_status === 'ARCHIVED'">
            <memlist-icon-button
              class="mr-2 mt-4 w-100"
              icon="fa fa-play"
              :text="$t('EVENT.PUBLISH')"
              variant="success"
              @click="publish_clicked"
            />
            
          </div>
          <div v-else-if="local_item_status === 'ACTIVE'">
            <memlist-icon-button
              class="mr-2 mt-4 w-100"
              icon="fa fa-archive"
              :text="$t('EVENT.ARCHIVE')"
              variant="primary"
              @click="archive_clicked"
            />
          </div>
          
        </div>

        <!-- fill the rest of the width -->
        <div class="content flex-grow-1 flex-fill">
          <memlist-card class="content-card d-flex flex-column flex-fill">
            <m-row>
              <m-col lg="12" sm="12">
                <p class="card-title-p">{{ creating ? ($t('EVENT.CREATING_EVENT') ) : ($t('EVENT.EDITING_EVENT')) }} </p>
              </m-col>

            </m-row>
            <hr />

            <EventFormBasic 
              v-if="local_item && selected_section_name === 'basic_info'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
              @publish="publish_clicked"
            />

            <EventFormLocation 
              v-if="local_item && selected_section_name === 'location'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
              @force_save="update_event"
            />

            <EventFormPublication 
              v-if="local_item && selected_section_name === 'publication'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
            />


            <EventFormTickets 
              v-if="local_item && selected_section_name === 'tickets'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
            />

            <EventFormLegacy 
              v-if="local_item && selected_section_name === 'legacy'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
            />

            <EventFormNotifications 
              v-if="local_item && selected_section_name === 'notifications'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
            />

            <EventFormParticipants
              v-if="local_item && selected_section_name === 'participants'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
            />

            <EventFormRecurring
              v-if="local_item && selected_section_name === 'recurring'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
              @recurring_created="recurring_created"
            />

            <EventFormQuestions
              v-if="local_item && selected_section_name === 'questions'"

              :item="local_item"
              :requirements="requirements"

              @change="event_changed"
              @update="update_event"
              @create="create_event"
            />

          </memlist-card>

        </div>

      </div>

      <m-row>
        <m-col cols="12">
          <RightModalSaveAndCloseButtons
            :text="$t('COMMON.SAVE')"
            :spin="true"
            @clicked="on_save_clicked"
            @close="$refs['internal-event-editor-modal'].hide()"
            ref="saveButton" />
        </m-col>
      </m-row>

    </div>

  </memlist-modal>

</template>


<script>

import axios from 'axios';
import { mapGetters } from 'vuex';

import { toasts } from '@/core/mixins/toastr-helper.mixin.js';
import { get_domain_url, get_base_url, downloadWithAxios } from '@/core/services/fileDownload';


import { is_mobile } from '@/core/services/utils';
import EventFormBasic from '@/view/pages/ml/events/forms/EventFormBasic.vue';
import EventFormParticipants from '@/view/pages/ml/events/forms/EventFormParticipants.vue';
import EventFormTickets from '@/view/pages/ml/events/forms/EventFormTickets.vue';
import EventFormLocation from '@/view/pages/ml/events/forms/EventFormLocation.vue';
import EventFormPublication from '@/view/pages/ml/events/forms/EventFormPublication.vue';
import EventFormNotifications from '@/view/pages/ml/events/forms/EventFormNotifications.vue';
import EventFormLegacy from '@/view/pages/ml/events/forms/EventFormLegacy.vue';
import EventFormQuestions from '@/view/pages/ml/events/forms/EventFormQuestions.vue';
import EventFormRecurring from '@/view/pages/ml/events/forms/EventFormRecurring.vue';


import ErrorPopup from '@/view/components/ErrorPopup.vue';
import validateRecurring from './recurringValidation.js';

export default {
  name: 'EventEditorModal',
  components: {
    EventFormBasic,
    EventFormParticipants,
    EventFormTickets,
    EventFormLocation,
    EventFormPublication,
    EventFormNotifications,
    EventFormLegacy,
    EventFormQuestions,
    ErrorPopup,
    EventFormRecurring
  },
  props: ['event_id'],
  emits: ['created', 'updated', 'deleted'],
  computed: {
    local_item_status() {
      if (this.waiting_for_status) {
        return null;
      }

      return this.local_item.status;
    },
    event_url() {
      return get_domain_url() + `/event/${this.local_item.event_id}`;
    },
    ...mapGetters(['selected_shop_item_id', 'currentCompanyId', 'currentPeriodId', 'currentUserId', 'companies', 'periods', 'profile', 'settings','isTHS', 'sid']),
    creating() {
      return !this.local_item || !this.local_item.event_id;
    },
    is_mobile() {
      return is_mobile();
    },
    selected_section_name() {
      if (this.selected_item === undefined) {
        return '';
      }

      if (this.unlocked_sections[this.selected_item] === undefined) {
        return '';
      }

      return this.unlocked_sections[this.selected_item].name;
    }
  },
  mixins: [ toasts ],
  watch: {
    
    event_id: {
      deep: true, // check properties of the form, not just the form reference (example.name for example)
      handler(val) {

        this.local_item = {}; // Clear the current local_item to prevent leftover state

        this.$nextTick(() => {
          if (this.event_id) {
            this.load_event(this.event_id);
          } else {
            this.local_item = { name: 'Nytt evenemang' };
          }
        });
      },
      immediate: true
    },
  },
  mounted() {
    if (is_mobile()) {
      this.mini = true;
    }

  },
  methods: {
    recurring_created(events) {
      this.$emit('recurring_created', events);
    },
    do_waiting_for_status() {
      this.waiting_for_status = true;

      setTimeout(()=>{
        this.waiting_for_status = false;
      }, 2000);
    },
    goto_url_clicked() {
      window.open(this.event_url, '_blank')
    },
    async archive_clicked() {
      this.do_waiting_for_status();
      try {
        this.local_item = { ...this.local_item, status: 'ARCHIVED' };

        // this is the row that triggeres the error, when when we comment out the this.on_save_clicked()
        this.$nextTick(async ()=>{
          await this.on_save_clicked();
        });
      }
      catch (err) {
        console.error('archive error', err);
      }
    },


    async publish_clicked() {

      const res = await axios.get(`/events/${this.local_item.event_id}/tickets`);
      if (res.status !== 200 || res.data.length === 0) {
        this.error_popup_message = 'ERR_EVENT_MUST_HAVE_TICKETS';

        this.$nextTick(()=>{ this.error_popup_message = null; });

        return;
      }


      if (!this.local_item.from_datetime) {
        this.error_popup_message = 'ERR_EVENT_MUST_HAVE_FROM_DATETIME';

        this.$nextTick(()=>{ this.error_popup_message = null; });

        return;
      }

      if (!this.local_item.num_seats) {
        this.error_popup_message = 'ERR_EVENT_MUST_HAVE_NUM_SEATS';

        this.$nextTick(()=>{ this.error_popup_message = null; });

        return;
      }

      
      this.do_waiting_for_status();
      
      try {
        this.local_item.status = 'ACTIVE';
        this.$nextTick(async ()=>{
          await this.on_save_clicked();
        });
      }
      catch (err) {
        console.error('publish error', err);
      }
    },

    async on_save_clicked() {
      this.$nextTick(async ()=>{

        if (this.local_item.recurring) {
          const validation = validateRecurring(this.local_item.recurring);

          if (!validation.ok) {
            this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_VALIDATE_RECURRING') + ' ' + validation.errors[0]);
            this.$refs['saveButton'].stop();
            return;
          }
        }

        
        if (this.local_item.event_id) {
          await this.update_event(this.local_item);
        }
        else {
          await this.create_event(this.local_item);
        }
      });
    },
    event_changed(item) {
      this.local_item = { ...item };
    },
    show() {
      this.selected_item = 0;
      this.$refs['internal-event-editor-modal'].show();
    },
    hide() {
      this.$refs['internal-event-editor-modal'].hide();
    },
    on_drawer_item_click({ index, value, text }) {
      this.selected_item = index
      this.list_item_group_change(index)
    },
    
    get_unlock_section_fields_by_name(name) {
      const obj = this.shown_settings_data.find(item => item.name === name);

      if (obj === undefined) {
        return [];
      }

      return obj.children;
    },

    has_unlocked_section(name) {
      const unlock = this.shown_settings_data.find(item => item.name === name);

      return unlock !== undefined && unlock.value;
    },

    save_data() {
      /// save data is only called when member exists already


      this.$nextTick(() => {

        if (this.creating) {
          //this.create_member(this.member);
        }
        else {
          this.update_member(this.member);
        }
      });

    },

    create_submit() {

      this.create_member(this.member);
    },

    async load_settings() {
      try {
        const result = await axios.get(`/company/${this.sid}`);

        if (result.status === 200) {
          this.shown_settings_data = result.data.company_settings.member_editor.editor_unlocks;
          this.requirements = result.data.requirements;

          for (const unlock of this.shown_settings_data) {
            if (!unlock.value) {
              continue;
            }
            this.unlocked_sections.push(this.all_sections.find(item => item.name === unlock.name));
          }

        }
      }
      catch (err) {
        console.error('load_settings error', err);
      }
    },

    async load_event(event_id) {
      try {
      
        const response = await axios.get(`/events/${event_id}`);

        if (response.status === 200) {
          this.local_item = response.data;
          console.log('local_item', this.local_item)
          return true;
        }

        console.error('unable to get event', response.status);

      }
      catch (err) {
        console.error('load_event', err);
      }

      return null;
    },

    async create_event(event) {
      try {

        const res = await axios.post(`/events`, {
          ...event,
          company_id: this.currentCompanyId,
          period_id: this.currentPeriodId });

        if (res.status === 403) {
          this.error_popup_message = 'ERR_FORBIDDEN';

          this.$nextTick(()=>{ this.error_popup_message = null; });
          return;
        }

        if (res.status !== 201) {
          this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_CREATE'));
          console.error('unable to create event', res.status);
          return;
        }
        else {
          this.toastr('success', this.$t('COMMON.OK'), this.$t('EVENT.CREATED'));
        }

        this.local_item = res.data;
        this.$emit('created', res.data);
      }
      catch (err) {
        console.error(`create_event error: ${err}`)
      }

      this.$refs['saveButton'].stop();
    },

    async update_event(event) {
      try {

        const res = await axios.put(`/events/${event.event_id}`, {
          ...event,
          company_id: this.currentCompanyId,
          period_id: this.currentPeriodId });

        if (res.status === 403) {
          this.error_popup_message = 'ERR_FORBIDDEN';

          this.$nextTick(()=>{ this.error_popup_message = null; });
          return;
        }

        if (res.status !== 200) {
          this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_UPDATE'));
          console.error('unable to update event', res.status);
          return;
        }
        else {
          this.toastr('success', this.$t('COMMON.OK'), this.$t('EVENT.UPDATED'))
        }

        this.$emit('updated', res.data);
      }
      catch (err) {
        console.error(`create_event error: ${err}`)
      }

      this.$refs['saveButton'].stop();
    },

    check_url(url) {
      if (!url || url === '' || url === 'https://') return '';
      if (url.indexOf('http://') < 0 && url.indexOf('https://') < 0) return 'https://' + url;
      else return url;
    },

    async save_event(e) {
      if (e !== undefined) {
        e.preventDefault();
      }

      try {
        this.local_item.company_id = this.currentCompanyId;
        this.local_item.facebook_url = this.check_url(this.local_item.facebook_url);
        this.local_item.extern_url = this.check_url(this.local_item.extern_url);
        this.local_item.max_tickets_per = parseInt(this.local_item.max_tickets_per+'');

        if (!Array.isArray(this.local_item.notif_user_ids)) {
          this.local_item.notif_user_ids = undefined;
        }

        const res = await axios.put(`/events/${this.local_item.event_id}`, this.local_item)

        if (res.status === 200) {
          this.$emit('update', this.local_item);
          this.toastr('success', this.$t('COMMON.OK'), this.$t('EVENT.UPDATED'));

        } else {
          console.error(res.status);
          this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_UPDATE'));
        }

      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_UPDATE'));
      }

      this.$refs['saveButton'].stop();
    },

    async generate_google_maps(addr, zipcode, postal) {
      try {
        const res = await axios.get(`/company/geo?address=${addr}&zipcode=${zipcode}&postaladdress=${postal}&country=SE`)
    
        if (res.status === 200) {
          this.toastr('success', 'Google Maps', this.$t('EVENT.SUCCESS_GOOGLE_MAPS'));

          this.local_item.google_maps = {
            lat: res.data.lat,
            lng: res.data.lng,
            static_link: ''
          };

          this.edit_lat = this.local_item.google_maps.lat;
          this.edit_lng = this.local_item.google_maps.lng;

          this.has_google_maps = true;
        }
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', 'Google Maps', this.$t('EVENT.UNABLE_GOOGLE_MAPS'));
      }
      
    },
    async delete_event(event_id) {

      try {

        const res = await axios.delete(`/events/${event_id}`);

        if (res.status === 403) {
          this.error_popup_message = 'ERR_FORBIDDEN';

          this.$nextTick(()=>{ this.error_popup_message = null; });
          return;
        }

        if (res.status === 204) {
          this.toastr('success', this.$t('COMMON.OK'), this.$t('EVENT.DELETED'));
          this.clear();
          this.$emit('deleted', member_id);
          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_DELETE'));
      }
      catch (err) {
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EVENT.UNABLE_DELETE'));
        console.error(err);
      }
    },

    toggle_mini() {
      if (is_mobile()) {
        this.mini = true;
        return;
      }

      this.mini = !this.mini;
    },

    list_item_group_change() {
      if (is_mobile()) {
        this.mini = true;
      }
    },

    clear() {

      this.local_item = {};
    },


  },
  data() {
    return {
      error_popup_message: null,
      waiting_for_status: false,
      selected_item: 0,
      mini: false,
      edit_lng: null,
      edit_lat: null,
      has_google_maps: false,
      
      error_popup_message: null,

      local_item: {

      },

      // which fields to show
      shown_settings_data: [],

      content_style: null,
      bottom_search_style: null,

      drawer: false,
      selected_item: 0,

      all_sections: [
        { title: this.$t('EVENT.BASIC'), icon: 'mdi-calendar-text', name: 'basic_info', hide_on_create: false },
        { title: this.$t('EVENT.TICKETS'), icon: 'mdi-ticket-confirmation', name: 'tickets', hide_on_create: true },
        //{ title: this.$t('EVENT.CATEGORIES'), icon: 'mdi-ticket-confirmation', name: 'categories', hide_on_create: true },
        { title: this.$t('EVENT.PUBLICATION'), icon: 'mdi-image', name: 'publication', hide_on_create: true },
        { title: this.$t('EVENT.RECURRING'), icon: 'mdi-calendar', name: 'recurring', hide_on_create: true },
        
        //{ title: this.$t('EVENT.ACCESS'), icon: 'mdi-lock-open-variant', name: 'availability', hide_on_create: true },
        { title: this.$t('EVENT.QUESTIONS'), icon: 'mdi-comment-question-outline', name: 'questions', hide_on_create: true },
        { title: this.$t('EVENT.LOCATION'), icon: 'mdi-map', name: 'location', hide_on_create: true },
        { title: this.$t('EVENT.NOTIFICATIONS'), icon: 'mdi-bell', name: 'notifications', hide_on_create: true },
        { title: this.$t('EVENT.LEGACY'), icon: 'mdi-history', name: 'legacy', hide_on_create: true },
        { title: this.$t('EVENT.PARTICIPANTS'), icon: 'mdi-account-group', name: 'participants', hide_on_create: true },
      ],


      // the final list of sections to show
      unlocked_sections: [
        { title: this.$t('EVENT.BASIC'), icon: 'mdi-calendar-text', name: 'basic_info', hide_on_create: false },
        { title: this.$t('EVENT.TICKETS'), icon: 'mdi-ticket-confirmation', name: 'tickets', hide_on_create: true },
        //{ title: this.$t('EVENT.CATEGORIES'), icon: 'mdi-tennis', name: 'categories', hide_on_create: true },
        { title: this.$t('EVENT.PUBLICATION'), icon: 'mdi-image', name: 'publication', hide_on_create: true },
        { title: this.$t('EVENT.RECURRING'), icon: 'mdi-calendar', name: 'recurring', hide_on_create: true },
        
        //{ title: this.$t('EVENT.ACCESS'), icon: 'mdi-lock-open-variant', name: 'availability', hide_on_create: true },
        { title: this.$t('EVENT.QUESTIONS'), icon: 'mdi-comment-question-outline', name: 'questions', hide_on_create: true },
        { title: this.$t('EVENT.LOCATION'), icon: 'mdi-map', name: 'location', hide_on_create: true },
        { title: this.$t('EVENT.NOTIFICATIONS'), icon: 'mdi-bell', name: 'notifications', hide_on_create: true },
        { title: this.$t('EVENT.LEGACY'), icon: 'mdi-history', name: 'legacy', hide_on_create: true },
        { title: this.$t('EVENT.PARTICIPANTS'), icon: 'mdi-account-group', name: 'participants', hide_on_create: true },
      ],

    }
  },
}

</script>

<style lang="scss" scoped>
@import "@/assets/sass/components/forms/_compact.scss";

.row {
  margin-top: -12px !important;
}

.page-container {
  height: 100%;
  display: flex;
  flex-flow: column;
}

.fill-rest {
  flex: 1 1 auto;
}


.quick-search-card {
  overflow: scroll;
}

.selected-item-active {
  border-radius: 4px;
  background: #f4fcff;
  color: black;
}

.card-title {
  background-color:#f7f7f7;
  border-bottom:1px solid #dedede;
  padding-bottom: 15px;

}

.card-title-p {
  margin-bottom: 8px !important;
  margin-top: 7px !important;
}


.top-container {
  display: flex;
}

.content {
  flex-grow: 1;
  margin-top: 0px !important;
  padding-top: 0px !important;
  margin-left: 8px !important;
}



.drawer-header {
  margin-bottom: -13px !important;
}

.drawer {
  border-right: 1px solid #dddddd !important;
}

.fixed-font {
  font-size: 0.95rem !important;
  font-weight: 500;
  line-height: 1rem;
}

:deep .v-application--wrap {
  min-height: 0px !important;
}

</style>
